import { useMemo } from 'react';
import { useUser } from './useUser';
import { NavItem } from '../common/components/Sidebar/Sidebar.types';
import { Module, Permission, SubModule, User } from '../types/user';
import { UserRoles } from '../common/constants';
import { ENO1_PERMISSIONS } from '../common/constants/permissionConstants';

// Sub-modules that should never appear in the sidebar navigation
const HIDDEN_SUBMODULES: string[] = [
  ENO1_PERMISSIONS.PLAYGROUND,
];

/**
 * Custom hook to generate navigation items based on user permissions and modules
 * Maps the user's modules and submodules to sidebar navigation items
 * Only displays items that have enabled=true
 * 
 * Subscribes to cached user data without fetching to prevent duplicate requests.
 * If user data is already being fetched by another component (like Home.tsx),
 * this will use that cached data and re-render when it becomes available.
 */
export function useNavItems(): NavItem[] {
  // Subscribe to cached user data without fetching - only use cached data
  // This prevents duplicate requests while still reacting to data updates
  // UserInformationProvider handles fetching, all other components read from cache
  const { data: userInfo } = useUser(false);
  const user = userInfo as unknown as User | undefined;

  return useMemo(() => {
    // Create a default static Home navigation item that will be added at the beginning
    const homeItem: NavItem = {
      title: 'Home',
      path: '/home'
    };

    if (!user || !user.modules) {
      return [homeItem];
    }

    // Check if the user is an admin or super user
    const isAdminOrSuperUser = user.role === UserRoles.ADMIN || user.role === UserRoles.SUPER_USER;

    // Transformer function to create NavItems from modules and their submodules
    const transformModuleToNavItem = (module: Module): NavItem | null => {
      // Skip disabled modules
      if (!module.enabled) {
        return null;
      }

      // For modules with submodules that we want to show as children
      if (module.subModules && module.subModules.length > 0) {
        // Filter and map enabled submodules to NavItem children
        const children = module.subModules
          .filter((subModule: SubModule) => subModule.enabled && !HIDDEN_SUBMODULES.includes(subModule.name))
          .map((subModule: SubModule) => ({
            title: subModule.name,
            path: subModule.url
          }));

        // If no enabled submodules exist, don't show the module
        if (children.length === 0) {
          return null;
        }

        return {
          title: module.name,
          children,
          path: module.url
        };
      }

      // For modules without submodules, just use the module data directly
      return {
        title: module.name,
        path: module.url
      };
    };

    // Create NavItems from enabled modules
    const moduleNavItems = user.modules
      .map(transformModuleToNavItem)
      .filter((item): item is NavItem => item !== null); // Filter out null results
    
    // Define the desired module order
    const moduleOrder = [
      'eNO1 Sampling',
      'e53 Sampling',
      'Sampling Planning',
      'e55 Weights',
      'e09 IBRS',
      'IPC TD Accounting',
      'UPU TD Accounting'
    ];
    
    // Sort the modules based on the defined order
    const sortedModuleNavItems = [...moduleNavItems].sort((a, b) => {
      const indexA = moduleOrder.indexOf(a.title);
      const indexB = moduleOrder.indexOf(b.title);
      
      // If neither is in the order array, maintain original order
      if (indexA === -1 && indexB === -1) return 0;
      
      // If one is not in the order array, place it after the ordered items
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      // Both are in the order array, sort by their positions
      return indexA - indexB;
    });
    
    // Create the final nav items array starting with Home
    let finalNavItems = [homeItem, ...sortedModuleNavItems];
    
    // Add Settings item at the end for all authenticated users
    const settingsItem: NavItem = {
      title: 'Settings',
      path: '/settings'
    };
    finalNavItems.push(settingsItem);
    
    return finalNavItems;
  }, [user]);
} 